'use client';

import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { ConfirmationContent } from '~/components/confirmation-content';
import { StepList } from '~/components/step-list';
import { TransactionStatusDisplay } from '~/components/transaction-status-display';
import { Button } from '~/components/ui';
import { TGS_STICKERS } from '~/constants/tgs-stickers';
import { useUserEventsStream } from '~/hooks/use-user-events-stream';
import { useNftControllerMintCollectionNft, useUsersControllerGetProfile } from '~/lib/api';
import type { PackResponseDto } from '~/lib/api';
import { getErrorMessage } from '~/lib/get-error-message';
import { NftFeeText } from '~/module/nft/nft-fee-text';

interface MintFlowProps {
  pack: PackResponseDto;
  onSuccess?: () => void;
}

export function MintFlow({ pack, onSuccess }: MintFlowProps) {
  const mintFee = '0.1';
  const t = useTranslations('my-collection.nft');
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [hasMintingCompleted, setHasMintingCompleted] = useState(false);
  const { data: user } = useUsersControllerGetProfile();

  const mint = useNftControllerMintCollectionNft({
    mutation: {
      onError: (error: unknown) => {
        if (error instanceof AxiosError) {
          // If it's a balance error (400), show the specific message
          if (error.status === 400 && error.response?.data) {
            const errorData = error.response.data as { message?: string };
            const errorMessage = errorData.message;
            if (errorMessage?.includes('Insufficient balance')) {
              setErrorMessage(errorMessage);
              return;
            }
          }
          // If it's conflict (409) - another transaction in progress
          if (error.status === 409) {
            setErrorMessage(t('mint.error-message'));
            return;
          }
        }
        setErrorMessage(getErrorMessage(error) ?? 'Unexpected error.');
      },
    },
  });

  const isMinting = mint.isSuccess;
  const isError = mint.isError || !!errorMessage;

  // Listen to user events for mint progress
  useUserEventsStream({
    userId: user?.data.id ?? '',
    onEvent: (event) => {
      if (event.type === 'NFT_AUDIT' && event.data.packId === pack.id) {
        const isCompleted =
          event.data.action === 'MINT_BLINDBOX_COMPLETED' || event.data.action === 'MINT_COLLECTION_COMPLETED';
        if (isCompleted) {
          void queryClient.invalidateQueries();
          setHasMintingCompleted(true);
          return;
        }

        const isFailed = event.data.action === 'MINT_BLINDBOX_FAILED' || event.data.action === 'MINT_COLLECTION_FAILED';
        if (isFailed) {
          setErrorMessage(t('mint.error-message'));
          return;
        }
      }
    },
  });

  const handleMint = () => {
    mint.mutate({ packId: pack.id });
  };

  const mintSteps = [
    { stepNumber: 1, description: t('mint.steps.step-1') },
    { stepNumber: 2, description: t('mint.steps.step-2') },
    { stepNumber: 3, description: t('mint.steps.step-3') },
    { stepNumber: 4, description: t('mint.steps.step-4') },
  ];

  if (hasMintingCompleted) {
    return (
      <TransactionStatusDisplay
        buttonText={t('actions.close')}
        message={t('mint.completed-message')}
        stickerSrc={TGS_STICKERS.HAPPY}
        title={t('mint.completed-title')}
        onButtonClick={onSuccess}
      />
    );
  }

  if (isError) {
    return (
      <TransactionStatusDisplay
        buttonText={t('actions.close')}
        message={t('mint.error-message')}
        stickerSrc={TGS_STICKERS.VOMIT}
        title={t('mint.error-title')}
        onButtonClick={onSuccess}
      />
    );
  }

  if (isMinting) {
    return (
      <TransactionStatusDisplay
        buttonText={t('actions.close')}
        message={t('mint.pending-message')}
        stickerSrc={TGS_STICKERS.HAPPY}
        title={t('mint.pending-title')}
        onButtonClick={onSuccess}
      />
    );
  }

  return (
    <div className="max-h-[90vh] grid grid-rows-[max-content_1fr]">
      <ConfirmationContent
        content={
          <div className="flex flex-col h-full">
            <div className="mb-6 overflow-y-auto">
              <StepList steps={mintSteps} />
            </div>
            <div className="mb-6">
              <NftFeeText fee={mintFee} feeType="MINTING" />
            </div>
            <div className="pb-6">
              <Button className="w-full" disabled={isMinting} variant="rainbow" onClick={handleMint}>
                {t('actions.mint')}
              </Button>
            </div>
          </div>
        }
        description={t('mint.confirm-description')}
        title={t('mint.confirm-title')}
      />
    </div>
  );
}
